import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Car, Zap, Box, MapPin, Users, Activity, Filter, Search } from 'lucide-react';
import { getGeoLocatedObjects, TYPE_LABELS } from '@/data/api';
import { getApiErrorMessage } from '@/lib/api';

// Color per object type — matches the rest of the app
const TYPE_META = {
  veiculo: { color: '#3b82f6', bg: '#dbeafe', Icon: Car, label: 'Veículo' },
  carregador: { color: '#eab308', bg: '#fef9c3', Icon: Zap, label: 'Carregador' },
  outro: { color: '#14b8a6', bg: '#ccfbf1', Icon: Box, label: 'Outro' },
};

const STATUS_COLOR = {
  active: '#10b981',
  maintenance: '#f59e0b',
  inactive: '#64748b',
};

// Build a small SVG pin icon per type+status without any external asset
const makeIcon = (type, status) => {
  const meta = TYPE_META[type] || TYPE_META.outro;
  const dot = STATUS_COLOR[status] || '#64748b';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52" width="40" height="52">
      <defs>
        <filter id="sh" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-opacity="0.3"/>
        </filter>
      </defs>
      <path filter="url(#sh)" d="M20 2 C9 2 2 10 2 20 C2 33 20 50 20 50 C20 50 38 33 38 20 C38 10 31 2 20 2 Z"
            fill="${meta.color}" stroke="#ffffff" stroke-width="2"/>
      <circle cx="20" cy="20" r="9" fill="#ffffff"/>
      <circle cx="20" cy="20" r="6" fill="${meta.color}"/>
      <circle cx="30" cy="8" r="5" fill="${dot}" stroke="#ffffff" stroke-width="1.5"/>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: 'custom-fleet-marker',
    iconSize: [40, 52],
    iconAnchor: [20, 50],
    popupAnchor: [0, -44],
  });
};

const MapViewUpdater = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, map.getZoom(), { animate: true });
  }, [map, center]);

  return null;
};

export const FleetMapPage = () => {
  const [allObjects, setAllObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState({ veiculo: true, carregador: true, outro: true });
  const [search, setSearch] = useState('');

  const loadGeoLocatedObjects = async () => {
    setLoading(true);
    setError('');
    try {
      setAllObjects(await getGeoLocatedObjects());
    } catch (loadError) {
      setError(getApiErrorMessage(loadError, 'Nao foi possível carregar as localizações da frota.'));
      setAllObjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const objects = await getGeoLocatedObjects();
        if (!cancelled) setAllObjects(objects);
      } catch (loadError) {
        if (!cancelled) {
          setError(getApiErrorMessage(loadError, 'Nao foi possível carregar as localizações da frota.'));
          setAllObjects([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = allObjects.filter(o => {
    if (!typeFilter[o.type]) return false;
    const q = search.toLowerCase();
    if (!q) return true;
    return String(o.identifier).toLowerCase().includes(q) || String(o.label).toLowerCase().includes(q);
  });

  // Center map on the centroid of filtered items (fallback: Lisbon)
  const center = useMemo(() => filtered.length > 0
    ? [
        filtered.reduce((s, o) => s + o.coords.lat, 0) / filtered.length,
        filtered.reduce((s, o) => s + o.coords.lng, 0) / filtered.length,
      ]
    : [38.7223, -9.1393], [filtered]);

  const toggleType = (t) => setTypeFilter(p => ({ ...p, [t]: !p[t] }));

  const counts = {
    veiculo: allObjects.filter(o => o.type === 'veiculo').length,
    carregador: allObjects.filter(o => o.type === 'carregador').length,
    outro: allObjects.filter(o => o.type === 'outro').length,
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="fleet-map-page">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Mapa da Frota</h1>
            <p className="text-slate-600 font-primary mt-1">
              Localização em tempo real dos objetos da frota com geolocalização
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Activity className="text-emerald-500" size={14} />
            {loading && <span>A carregar localizacoes...</span>}
            <span>{!loading && `${filtered.length} objeto(s) visíveis de ${allObjects.length}`}</span>
            <Button variant="outline" size="sm" onClick={loadGeoLocatedObjects} disabled={loading}>
              Atualizar
            </Button>
          </div>
        </div>

        {/* Filters bar */}
        <Card className="p-4" data-testid="map-filters">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[240px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <Input placeholder="Pesquisar por referência, código ou modelo..." className="pl-9"
                     value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500"><Filter size={14} />Tipos:</div>
            {Object.entries(TYPE_META).map(([key, meta]) => {
              const Icon = meta.Icon;
              const active = typeFilter[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleType(key)}
                  data-testid={`filter-type-${key}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 transition-all text-sm ${active ? 'border-transparent shadow-sm' : 'border-slate-200 opacity-50'}`}
                  style={active ? { backgroundColor: meta.bg, color: meta.color } : {}}
                >
                  <Icon size={14} />
                  <span className="font-semibold">{meta.label}</span>
                  <Badge variant="outline" className="text-[10px] bg-white">{counts[key]}</Badge>
                </button>
              );
            })}
          </div>
        </Card>

        {error && (
          <Card className="p-4 border-red-200 bg-red-50 text-sm text-red-700" data-testid="map-error">
            {error}
          </Card>
        )}

        {/* Map */}
        <Card className="overflow-hidden" data-testid="map-container-card">
          <div className="relative" style={{ height: '620px', width: '100%' }}>
            <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
              <MapViewUpdater center={center} />
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="OpenStreetMap">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Satélite (Esri)">
                  <TileLayer
                    attribution='Tiles &copy; Esri'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />
                </LayersControl.BaseLayer>
              </LayersControl>

              {filtered.map(o => {
                const meta = TYPE_META[o.type] || TYPE_META.outro;
                const Icon = meta.Icon;
                return (
                  <Marker key={o.uid} position={[o.coords.lat, o.coords.lng]} icon={makeIcon(o.type, o.status)}>
                    <Popup>
                      <div className="min-w-[220px] -m-1">
                        <div className="flex items-center gap-2 pb-2 border-b border-slate-200 mb-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: meta.bg }}>
                            <Icon size={16} style={{ color: meta.color }} />
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 text-sm leading-tight">{o.label}</p>
                            <p className="text-[11px] text-slate-500 font-mono">{o.identifier}</p>
                          </div>
                        </div>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500">Tipo:</span>
                            <Badge variant="outline" className="text-[10px]">{TYPE_LABELS[o.type] || o.type}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500">Estado:</span>
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLOR[o.status] }} />
                            <span className="text-slate-700 font-medium capitalize">{o.status}</span>
                          </div>
                          {o.category && (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">Categoria:</span>
                              <span className="text-slate-700">{o.category}</span>
                            </div>
                          )}
                          {o.driver && (
                            <div className="flex items-center gap-2">
                              <Users size={10} className="text-emerald-600" />
                              <span className="text-slate-700 font-medium">{o.driver}</span>
                            </div>
                          )}
                          {o.extra && (
                            <div className="flex items-start gap-2">
                              <MapPin size={10} className="text-slate-400 mt-0.5" />
                              <span className="text-slate-600 text-[11px]">{o.extra}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 pt-1 mt-1 border-t border-slate-100 text-[10px] text-slate-400 font-mono">
                            {o.coords.lat.toFixed(4)}, {o.coords.lng.toFixed(4)}
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
            {(loading || (!error && !loading && allObjects.length === 0)) && (
              <div className="absolute inset-x-0 top-4 z-[500] flex justify-center pointer-events-none">
                <div className="rounded-lg bg-white/95 border border-slate-200 shadow-sm px-4 py-2 text-sm text-slate-600">
                  {loading ? 'A carregar localizações da API...' : 'Nenhum objeto com geolocalização encontrado.'}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Legend */}
        <Card className="p-4" data-testid="map-legend">
          <div className="flex flex-wrap items-center gap-6 text-xs">
            <span className="font-semibold text-slate-700 uppercase tracking-wide text-[10px]">Legenda</span>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-500" />Ativo</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-amber-500" />Manutenção</div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-slate-500" />Inativo</div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};
